import React, {useEffect, useState} from 'react';
import {Dialog, Popover} from '@headlessui/react';
import {Bars3Icon,XMarkIcon} from '@heroicons/react/24/outline'
import {Link, useLocation} from "react-router-dom";

const NAV_DARK = '#333D29';
const NAV_LIGHT = '#f9ffdf';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const hexToRgb = (hex) => {
    const h = hex.replace('#','');
    const full = h.length === 3 ? h.split('').map(c=>c+c).join('') : h;
    const n = parseInt(full,16);
    return { r: (n>>16)&255, g:(n>>8)&255, b: n&255 };
};
const mixHexColors = (from, to, t) => {
    const a = hexToRgb(from);
    const b = hexToRgb(to);
    const tt = clamp(t,0,1);
    const r = Math.round(a.r + (b.r - a.r)*tt);
    const g = Math.round(a.g + (b.g - a.g)*tt);
    const bl = Math.round(a.b + (b.b - a.b)*tt);
    return `rgb(${r}, ${g}, ${bl})`;
};

const elementi = [
    { name: 'Home', href: '#home', type: 'anchor' },
    { name: 'About', href: '#about', type: 'anchor' },
    { name: 'Projects', href: '#projects', type: 'anchor' },
    { name: 'Skills', href: '#skills', type: 'anchor' },
    { name: 'Wallpapers', to: '/wallpapers', type: 'route' },
]

export default function Navbar() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showSolidBackground, setShowSolidBackground] = useState(false);
    const [navbarTextColor, setNavbarTextColor] = useState(NAV_DARK);
    const location = useLocation();
    const isHome = location.pathname === '/';

    useEffect(() => {
        const updateNavbarBackground = () => {
            const hero = document.getElementById('home');
            if (!isHome || !hero) {
                setShowSolidBackground(true);
                setNavbarTextColor(NAV_LIGHT);
                return;
            }

            const navbarOffset = 80;
            const triggerPoint = Math.max(window.innerHeight - navbarOffset, 0);
            setShowSolidBackground(window.scrollY >= triggerPoint);

            const heroTop = hero.offsetTop;
            const heroHeight = hero.offsetHeight;
            const navbarMarker = window.scrollY + 72; // approximate navbar vertical marker
            const colorStart = heroTop + heroHeight * 0.15;
            const colorEnd = heroTop + heroHeight * 0.85; // quicker transition
            const progress = (navbarMarker - colorStart) / (colorEnd - colorStart);
            setNavbarTextColor(mixHexColors(NAV_DARK, NAV_LIGHT, progress));
        };

        updateNavbarBackground();
        window.addEventListener('scroll', updateNavbarBackground, {passive: true});
        window.addEventListener('resize', updateNavbarBackground);

        return () => {
            window.removeEventListener('scroll', updateNavbarBackground);
            window.removeEventListener('resize', updateNavbarBackground);
        };
    }, [isHome, location.pathname]);

    useEffect(() => {
        const hash = location.hash;
        // Only scroll if we're on the home page and there's a valid hash
        if (hash && isHome) {
            const id = hash.replace('#', '');
            setTimeout(() => {
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        }
    }, [location.hash, isHome]);

    const handleAnchorClick = (href, e) => {
        // href expected like '#about'
        if (e) e.preventDefault();
        if (!isHome) {
            // Navigate to home with the anchor hash in the URL
            window.location.href = '/' + href;
            return;
        }

        const id = href.replace('#','');
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setMobileMenuOpen(false);
    };

    const handleContact = (e) => handleAnchorClick('#contact', e);

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 text-my-black-2 dark:text-white ${
                showSolidBackground
                    ? 'bg-opacity-75 backdrop-blur bg-earth-800'
                    : 'bg-transparent shadow-none'
            }`}
            style={{ color: navbarTextColor }}
        >
            <nav className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-6" aria-label="Global">
                <div className="flex lg:hidden">
                    <button
                        type="button"
                        className="-m-2 inline-flex items-center justify-center rounded-md p-2"
                        onClick={() => setMobileMenuOpen(prev => !prev)}
                        aria-expanded={mobileMenuOpen}
                        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                    >
                        {mobileMenuOpen ? (
                            <XMarkIcon className="h-6 w-6" style={{ color: navbarTextColor }} aria-hidden="true" />
                        ) : (
                            <Bars3Icon className="h-6 w-6" style={{ color: navbarTextColor }} aria-hidden="true" />
                        )}
                    </button>
                </div>

                <Popover.Group className="hidden lg:flex lg:gap-x-8">
                    {elementi.map((item, i) => (
                        item.type === 'route' ? (
                            <Link key={i} to={item.to} className="rounded-lg py-1.5 px-2 text-base lg:text-lg font-semibold">
                                {item.name}
                            </Link>
                        ) : (
                            <a
                                key={i}
                                href={item.href}
                                onClick={(e) => handleAnchorClick(item.href, e)}
                                className="rounded-lg py-1.5 px-2 text-base lg:text-lg font-semibold"
                            >
                                {item.name}
                            </a>
                        )
                    ))}
                </Popover.Group>

                <div className="hidden lg:flex lg:flex-1 lg:justify-end">
                    <a
                        href="#contact"
                        onClick={handleContact}
                        className="rounded-lg py-1.5 px-2 text-base lg:text-lg font-semibold"
                    >
                        Contact
                    </a>
                </div>
            </nav>

            <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
                <div className="fixed inset-0 z-30" onClick={() => setMobileMenuOpen(false)}/>
                <Dialog.Panel className="fixed inset-y-0 right-0 z-30 w-full overflow-y-auto bg-my-blue-2 px-6 py-5 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10 shadow-md shadow-primary-300 dark:bg-my-blue dark:shadow-primary-700">

                    <div className="flex items-center justify-end">
                        <button type="button" className="-m-2 rounded-md p-2 text-gray-700" onClick={() => setMobileMenuOpen(false)}>
                            <span className="sr-only">Close menu</span>
                            <XMarkIcon className="h-6 w-6 text-black dark:text-gray-300" aria-hidden="true"/>
                        </button>
                    </div>

                    <div className="mt-4 flow-root">
                        <div className="space-y-1 py-4">
                            {elementi.map((item, i) => (
                                item.type === 'route' ? (
                                    <Link key={i} to={item.to} onClick={() => setMobileMenuOpen(false)} className="block rounded-lg py-2 px-3 text-base font-semibold text-black hover:text-primary-400 dark:text-white dark:hover:text-primary-400">{item.name}</Link>
                                ) : (
                                    <a key={i} href={item.href} onClick={(e) => handleAnchorClick(item.href, e)} className="block rounded-lg py-2 px-3 text-base font-semibold text-black hover:text-primary-400 dark:text-white dark:hover:text-primary-400">{item.name}</a>
                                )
                            ))}

                            <a href="#contact" onClick={handleContact} className="block rounded-lg py-2 px-3 text-base font-semibold text-black hover:text-primary-400 dark:text-white dark:hover:text-primary-400">Contact</a>
                        </div>
                    </div>
                </Dialog.Panel>
            </Dialog>
        </header>
    )
}
